<template>
  <v-container fill-height>
    <!-- Loader -->
    <div v-if="isLoading" class="d-flex align-center justify-center flex-column fill-height">
      <v-progress-circular indeterminate :color="colors.primary" size="64"></v-progress-circular>
    </div>

    <div v-else>
      <v-app-bar :elevation="2" :style="{ backgroundColor: colors.primary }">
        <v-app-bar-title :style="{ color: colors.secondary }">{{ $t('evaluation.title') }}</v-app-bar-title>

        <LanguageSwitcher />

        <v-menu>
          <template v-slot:activator="{ props }">
            <v-btn icon v-bind="props">
              <v-avatar size="36px">
                <v-img alt="Jury" :src="getJuryImageUrl(jury?.image)"></v-img>
              </v-avatar>
            </v-btn>
          </template>
          <v-list>
            <v-list-item @click="goToEvaluation">
              <v-list-item-title>{{ $t('menu.evaluation') }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="goToResults">
              <v-list-item-title>{{ $t('menu.results') }}</v-list-item-title>
            </v-list-item>
            <v-list-item @click="logoutUser">
              <v-list-item-title>{{ $t('menu.logout') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-app-bar>

      <v-container class="mt-5">
        <v-row justify="center">
          <v-col cols="12" sm="10" md="8" lg="6">
            <v-card class="pa-6" elevation="4">
              <v-card-title class="text-center mb-4">
                <h2>{{ $t('profile.title') }}</h2>
              </v-card-title>

              <v-form ref="profileForm" @submit.prevent="updateProfile">
                <!-- Current Profile Image -->
                <div class="text-center mb-4">
                  <v-avatar size="150">
                    <v-img
                      :src="imagePreview || getJuryImageUrl(form.image)"
                      alt="Profile"
                    ></v-img>
                  </v-avatar>
                </div>

                <!-- Image Upload -->
                <v-file-input
                  v-model="imageFile"
                  :label="$t('profile.changePhoto')"
                  prepend-icon="mdi-camera"
                  accept="image/*"
                  @change="handleImageChange"
                  :error-messages="imageErrors"
                  variant="outlined"
                  class="mb-3"
                ></v-file-input>

                <!-- Name Field -->
                <v-text-field
                  v-model="form.name"
                  :label="$t('profile.name')"
                  prepend-inner-icon="mdi-account"
                  :rules="nameRules"
                  :error-messages="nameErrors"
                  variant="outlined"
                  required
                  @input="clearNameError"
                  class="mb-3"
                ></v-text-field>

                <!-- Email Field -->
                <v-text-field
                  v-model="form.email"
                  :label="$t('profile.email')"
                  type="email"
                  prepend-inner-icon="mdi-email"
                  :rules="emailRules"
                  :error-messages="emailErrors"
                  variant="outlined"
                  required
                  @input="clearEmailError"
                  class="mb-3"
                ></v-text-field>

                <v-divider class="my-4"></v-divider>

                <v-card-subtitle class="px-0">
                  {{ $t('profile.changePassword') }}
                </v-card-subtitle>

                <!-- Current Password -->
                <v-text-field
                  v-model="form.current_password"
                  :label="$t('profile.currentPassword')"
                  :type="showCurrentPassword ? 'text' : 'password'"
                  prepend-inner-icon="mdi-lock"
                  :append-inner-icon="showCurrentPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append-inner="showCurrentPassword = !showCurrentPassword"
                  :error-messages="currentPasswordErrors"
                  variant="outlined"
                  @input="clearCurrentPasswordError"
                  class="mb-3"
                ></v-text-field>

                <!-- New Password -->
                <v-text-field
                  v-model="form.new_password"
                  :label="$t('profile.newPassword')"
                  :type="showNewPassword ? 'text' : 'password'"
                  prepend-inner-icon="mdi-lock-reset"
                  :append-inner-icon="showNewPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append-inner="showNewPassword = !showNewPassword"
                  :rules="passwordRules"
                  :error-messages="newPasswordErrors"
                  variant="outlined"
                  @input="clearNewPasswordError"
                  class="mb-3"
                ></v-text-field>

                <!-- Confirm Password -->
                <v-text-field
                  v-model="form.new_password_confirmation"
                  :label="$t('profile.confirmPassword')"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  prepend-inner-icon="mdi-lock-check"
                  :append-inner-icon="showConfirmPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append-inner="showConfirmPassword = !showConfirmPassword"
                  :rules="confirmPasswordRules"
                  :error-messages="confirmPasswordErrors"
                  variant="outlined"
                  @input="clearConfirmPasswordError"
                  class="mb-3"
                ></v-text-field>

                <!-- Success Alert -->
                <v-alert v-if="successMessage" type="success" class="mb-4" closable @click:close="successMessage = ''">
                  {{ successMessage }}
                </v-alert>

                <!-- Error Alert -->
                <v-alert v-if="errorMessage" type="error" class="mb-4" closable @click:close="errorMessage = ''">
                  {{ errorMessage }}
                </v-alert>

                <!-- Action Buttons -->
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-btn
                      type="submit"
                      :color="colors.primary"
                      block
                      size="large"
                      :loading="updating"
                      :disabled="updating"
                    >
                      {{ $t('profile.update') }}
                    </v-btn>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-btn
                      @click="goToEvaluation"
                      color="grey"
                      block
                      size="large"
                      variant="outlined"
                    >
                      {{ $t('profile.cancel') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      <v-footer border app class="d-flex justify-center align-center p-4">
        {{ $t('common.developedBy') }} : <a href="https://thewebsquare.com" target="_blank" :style="{ color: colors.primary }">The Web Square</a>
      </v-footer>
    </div>
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import axios from 'axios';
import api from '../api';
import colors from '../styles/colors';
import LanguageSwitcher from './LanguageSwitcher.vue';

export default {
  name: 'JuryProfile',
  components: {
    LanguageSwitcher,
  },
  data() {
    return {
      colors,
      isLoading: true,
      updating: false,
      imageFile: null,
      imagePreview: null,
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      successMessage: '',
      errorMessage: '',

      form: {
        name: '',
        email: '',
        image: '',
        current_password: '',
        new_password: '',
        new_password_confirmation: '',
      },

      // Error messages
      nameErrors: [],
      emailErrors: [],
      imageErrors: [],
      currentPasswordErrors: [],
      newPasswordErrors: [],
      confirmPasswordErrors: [],
    };
  },
  computed: {
    ...mapState(['jury']),
    // Validation rules
    nameRules() {
      return [
        v => !!v || this.$t('profile.nameRequired'),
        v => (v && v.length >= 2) || this.$t('profile.nameMinLength'),
      ];
    },
    emailRules() {
      return [
        v => !!v || this.$t('profile.emailRequired'),
        v => /.+@.+\..+/.test(v) || this.$t('profile.emailInvalid'),
      ];
    },
    passwordRules() {
      return [
        v => !v || v.length >= 8 || this.$t('profile.passwordMinLength'),
      ];
    },
    confirmPasswordRules() {
      return [
        v => !this.form.new_password || !!v || this.$t('profile.confirmRequired'),
        v => !this.form.new_password || v === this.form.new_password || this.$t('profile.passwordMismatch'),
      ];
    },
  },
  methods: {
    ...mapActions(['updateJury', 'logout']),

    clearNameError() {
      this.nameErrors = [];
      this.errorMessage = '';
    },
    clearEmailError() {
      this.emailErrors = [];
      this.errorMessage = '';
    },
    clearCurrentPasswordError() {
      this.currentPasswordErrors = [];
      this.errorMessage = '';
    },
    clearNewPasswordError() {
      this.newPasswordErrors = [];
      this.errorMessage = '';
    },
    clearConfirmPasswordError() {
      this.confirmPasswordErrors = [];
      this.errorMessage = '';
    },

    handleImageChange(event) {
      const file = event.target.files?.[0] || this.imageFile?.[0];
      if (file) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          this.imageErrors = [this.$t('profile.imageTypeError')];
          this.imageFile = null;
          return;
        }

        // Validate file size (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
          this.imageErrors = [this.$t('profile.imageSizeError')];
          this.imageFile = null;
          return;
        }

        this.imageErrors = [];

        // Create preview
        const reader = new FileReader();
        reader.onload = (e) => {
          this.imagePreview = e.target.result;
        };
        reader.readAsDataURL(file);
      } else {
        this.imagePreview = null;
      }
    },

    async updateProfile() {
      // Reset messages
      this.successMessage = '';
      this.errorMessage = '';
      this.nameErrors = [];
      this.emailErrors = [];
      this.currentPasswordErrors = [];
      this.newPasswordErrors = [];
      this.confirmPasswordErrors = [];

      // Validate form
      const { valid } = await this.$refs.profileForm.validate();
      if (!valid) {
        return;
      }

      // If changing password, current password is required
      if (this.form.new_password && !this.form.current_password) {
        this.currentPasswordErrors = [this.$t('profile.currentPasswordRequired')];
        return;
      }

      // Password confirmation check
      if (this.form.new_password && this.form.new_password !== this.form.new_password_confirmation) {
        this.confirmPasswordErrors = [this.$t('profile.passwordMismatch')];
        return;
      }

      this.updating = true;

      try {
        const data = {
          name: this.form.name,
          email: this.form.email,
        };

        // Add password fields if provided
        if (this.form.current_password && this.form.new_password) {
          data.current_password = this.form.current_password;
          data.password = this.form.new_password;
          data.password_confirmation = this.form.new_password_confirmation;
        }

        // Add base64 image if changed
        if (this.imagePreview) {
          data.image = this.imagePreview;
        }

        const response = await axios.post(api.juryUpdateProfile, data);

        if (response.data.success) {
          this.successMessage = this.$t('profile.successMessage');
          
          // Update Vuex store with new data
          this.updateJury(response.data.data);

          // Reset password fields
          this.form.current_password = '';
          this.form.new_password = '';
          this.form.new_password_confirmation = '';
          this.imageFile = null;
          this.imagePreview = null;

          // Update form with new data
          this.form.name = response.data.data.name;
          this.form.email = response.data.data.email;
          this.form.image = response.data.data.image;

          // Scroll to top to show success message
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      } catch (error) {
        console.error('Profile update error:', error);

        if (error.response) {
          if (error.response.status === 422) {
            const errors = error.response.data.errors;
            if (errors) {
              if (errors.name) this.nameErrors = Array.isArray(errors.name) ? errors.name : [errors.name];
              if (errors.email) this.emailErrors = Array.isArray(errors.email) ? errors.email : [errors.email];
              if (errors.current_password) this.currentPasswordErrors = Array.isArray(errors.current_password) ? errors.current_password : [errors.current_password];
              if (errors.password) this.newPasswordErrors = Array.isArray(errors.password) ? errors.password : [errors.password];
              if (errors.image) this.imageErrors = Array.isArray(errors.image) ? errors.image : [errors.image];
            }
            this.errorMessage = error.response.data.message || this.$t('profile.validationError');
          } else {
            this.errorMessage = error.response.data.message || this.$t('profile.updateError');
          }
        } else {
          this.errorMessage = this.$t('profile.serverError');
        }
      } finally {
        this.updating = false;
      }
    },

    async logoutUser() {
      try {
        await axios.post(api.juryLogout);
      } catch (error) {
        console.error('Logout error:', error);
      } finally {
        this.logout();
        this.$nextTick(() => {
          this.$router.push({ name: 'JuryLogin' });
        });
      }
    },

    getJuryImageUrl(filename) {
      return api.getJuryImageUrl(filename);
    },

    goToEvaluation() {
      this.$router.push({ name: 'EvaluationScreen' });
    },

    goToResults() {
      this.$router.push({ name: 'ResultsScreen' });
    },

    loadJuryData() {
      if (this.jury) {
        this.form.name = this.jury.name;
        this.form.email = this.jury.email;
        this.form.image = this.jury.image;
        this.isLoading = false;
      }
    },
  },
  mounted() {
    this.loadJuryData();
  },
};
</script>

<style scoped>
.fill-height {
  min-height: 100vh;
}
</style>